import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { addDays } from 'date-fns/addDays'

import { useConfig } from '../../../../modules/config'
import { type UpdateMovePayload, useCurrentMove } from '../../../../modules/moves'
import { ArrivalWindow } from '../../../../common/arrivalWindow'
import Form, {
  CalendarInput,
  CardCheckboxGroup,
  CardLabel,
} from '../../../Form'
import SubmitBar from '../SubmitBar'
import { Label, Row, Cell, Field } from '../../../data'
import useValidationSchema from './validationSchema'
import { moveToFormData, formatFormOutput } from './formDataBuilder'
import { type DateTimeFormValues } from './DateTimeForm.type'
import { mergeClassName } from '../../../../utils/mergeClassName'
import { getJobLabel } from '../../../../modules/moves/core/resolved.utils'

type DateTimeFormProps = {
  onSubmit: (data: UpdateMovePayload) => Promise<void> | void
  onCancel?: () => void
}

const DateTimeForm: React.FC<DateTimeFormProps> = ({
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation()
  const now = new Date()
  const move = useCurrentMove()

  const schema = useValidationSchema()
  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: moveToFormData(move),
  })

  const minDateDays = useConfig<number>('instantBooking.quote.minDateDays')
  const maxDateDays = useConfig<number>('instantBooking.quote.maxDateDays')

  const multiDays = move.jobs.length > 1

  const preferredTimeOptions = Object.fromEntries(Object.values(ArrivalWindow).map(arrivalWindow => ([
    arrivalWindow,
    <CardLabel
      key={arrivalWindow}
      title={t(`arrivalWindows.${arrivalWindow}.title`)}
    />,
  ])))

  const handleSubmit = async (values: DateTimeFormValues) => { await onSubmit(formatFormOutput(values)) }

  return (
    <Form
      form={form}
      onSubmit={handleSubmit}
    >
      { move.jobs.map((job, index) => {
        const jobLabel = getJobLabel(move, job.id, t)

        return (
          <div key={job.id}>
            { multiDays && (
              <div
                className={mergeClassName(
                  'mb-2 font-body2 text-lg text-black sm:mb-0 py-2',
                  index > 0 && 'border-t pt-6 mt-6',
                )}
              >
                { jobLabel }
              </div>
            ) }
            <Row>
              <Cell>
                <Label>
                  { multiDays
                    ? t('components.move.dateTime.jobDate')
                    : t('components.move.dateTime.movingDate') }
                </Label>
                <Field>
                  <CalendarInput
                    name={`jobs.${index}.dateTime.startDate`}
                    wrapperClassName="shadow ml-[-15px] sm:ml-0 lg:mr-4"
                    minDate={addDays(now, minDateDays + 1)}
                    maxDate={addDays(now, maxDateDays)}
                  />
                </Field>
              </Cell>
              <Cell>
                <Label>
                  { t('components.move.dateTime.preferredTime') }
                </Label>
                <Field>
                  <CardCheckboxGroup
                    name={`jobs.${index}.dateTime.preferredTimeRanges`}
                    options={preferredTimeOptions}
                    small
                  />
                </Field>
              </Cell>
            </Row>
          </div>
        )
      }) }

      <SubmitBar onCancel={onCancel} />
    </Form>
  )
}

export default DateTimeForm
